import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Button,
  Chip,
  useTheme,
  useMediaQuery,
  InputBase,
  alpha,
  Badge,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Search as SearchIcon,
  AccountCircle,
  Settings as SettingsIcon,
  ExitToApp as LogoutIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  Dashboard as DashboardIcon,
  Person as PersonIcon,
  Help as HelpIcon,
  Brightness4 as ThemeIcon,
} from '@mui/icons-material';
import NotificationCenter from './NotificationCenter';
import GlobalSearch from './GlobalSearch';
import MovdLogo from './MovdLogo.jsx';

const EnhancedAppBar = ({ 
  onMenuToggle, 
  profile, 
  onLogout, 
  darkMode, 
  onThemeToggle,
  token,
  title,
  onQuickAction,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchValue, setSearchValue] = useState('');

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNavigation = (path) => {
    navigate(path);
    handleProfileMenuClose();
  };

  const handleLogout = () => {
    onLogout?.();
    handleProfileMenuClose();
  };

  const getPageTitle = () => {
    if (title) return title;
    
    const path = location.pathname;
    const segments = path.split('/').filter(Boolean);
    const lastSegment = segments[segments.length - 1];
    
    const titleMap = {
      'dashboard': 'Dashboard',
      'bookings': 'Bookings',
      'calendar': 'Availability & calendar',
      'profile': 'Profile',
      'pricing': 'Pricing',
      'reviews': 'Reviews',
      'payments': 'Earnings & payouts',
      'settings': 'Settings',
      'team': 'Team',
      'analytics': 'Analytics',
      'trucks': 'Truck Rental',
      'single-item': 'Single Items',
      'hotshot': 'Hotshot Services',
    };
    
    return titleMap[lastSegment] || 'Mover Portal';
  };

  const getServiceBadges = () => {
    if (!profile?.services) return null;
    
    return profile.services.slice(0, 2).map(service => (
      <Chip
        key={service}
        label={service}
        size="small"
        variant="outlined"
        sx={{ 
          ml: 0.5,
          height: 20,
          fontSize: '0.7rem',
          textTransform: 'capitalize',
        }}
      />
    ));
  };

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        zIndex: theme.zIndex.drawer + 1,
        backgroundColor: '#FFFFFF',
        color: 'text.primary',
        borderBottom: '1px solid rgba(15,23,42,0.06)',
      }}
    >
      <Toolbar sx={{ minHeight: '58px !important', px: { xs: 2, md: 3 }, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {isMobile && <IconButton onClick={onMenuToggle} size="small" aria-label="Open navigation"><MenuIcon /></IconButton>}
          <MovdLogo size="small" variant="full" sx={{ display: { xs: 'none', md: 'block' }, width: 78, flexShrink: 0 }} />
          <Box sx={{ display: { xs: 'none', md: 'block' }, borderLeft: '1px solid #DCE9EA', pl: 1.75 }}>
            <Typography sx={{ color: '#173057', fontSize: '.82rem', fontWeight: 800, lineHeight: 1.1 }}>Mover Partner Portal</Typography>
            <Typography sx={{ color: '#60748D', fontSize: '.58rem', mt: .25 }}>Manage quotes. Grow your business.</Typography>
          </Box>
        </Box>

        <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: .75, alignItems: 'center' }}>
          <Typography sx={{ color: '#60748D', fontSize: '.68rem', fontWeight: 700 }}>Your partner workspace</Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {profile && !profile.authorized && (
            <Typography sx={{ display: { xs: 'none', sm: 'block' }, color: '#A35C00', background: '#FFF5DF', border: '1px solid #F2D39A', borderRadius: 1, px: 1, py: .5, fontSize: '.65rem', fontWeight: 800 }}>
              Profile under review
            </Typography>
          )}
          {!profile && (
            <Button href="/?portal=mover-login" variant="text" sx={{ textTransform: 'none' }}>Mover Login</Button>
          )}
          {!profile && (
            <Button href="/become-partner" variant="contained" sx={{ backgroundColor: '#176B60', color: '#fff', '&:hover': { backgroundColor: '#0F4C3A' }, textTransform: 'none' }}>Become a Partner</Button>
          )}

          {profile && (
            <Tooltip title="Account settings">
              <IconButton onClick={handleProfileMenuOpen} sx={{ ml: 1 }}>
                <Avatar src={profile?.profilePhoto} sx={{ width: 28, height: 28, bgcolor: '#E1F7F3', color: '#008D7B', fontSize: '.7rem', fontWeight: 800 }}>{profile?.name?.charAt(0) || 'M'}</Avatar>
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default EnhancedAppBar;